import {
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useMemo, useState } from 'react';
import { updateTenderSectionIndex } from '../../../services/tenderSectionService';
import type { Section, Tender } from '../../../types/tender';
import { logTenderActivity } from '../../../utils/activityLogger';

export function useSectionDnD(
  sections: Section[],
  tenderId: string,
  tender: Tender,
  reorderSections: (sections: Section[]) => void
) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(new Set());

  const visibleSections = useMemo(() => {
    if (!activeId) {
      return sections;
    }
    const activeIndex = sections.findIndex((s) => s.id === activeId);
    if (activeIndex === -1) {
      return sections;
    }

    const activeSection = sections[activeIndex];
    if (!activeSection) {
      return sections;
    }

    const activeLevel = activeSection.level || 2;
    if (activeLevel === 1) {
      return sections.filter((s) => (s.level || 2) === 1);
    }

    const hiddenIds = new Set<string>();
    let i = activeIndex + 1;
    while (i < sections.length) {
      const s = sections[i];
      if (s && (s.level || 2) > activeLevel) {
        hiddenIds.add(s.id);
        i++;
      } else {
        break;
      }
    }
    return sections.filter((s) => !hiddenIds.has(s.id));
  }, [sections, activeId]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    setExpandedSectionIds(new Set());
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sections.findIndex((s) => s.id === active.id);
    if (oldIndex === -1) {
      return;
    }

    const activeSection = sections[oldIndex];
    if (!activeSection) {
      return;
    }

    const activeLevel = activeSection.level || 2;
    const block = [activeSection];
    let i = oldIndex + 1;
    while (i < sections.length) {
      const nextSec = sections[i];
      if (nextSec && (nextSec.level || 2) > activeLevel) {
        block.push(nextSec);
        i++;
      } else {
        break;
      }
    }

    const remainingSections = sections.filter((s) => !block.some((b) => b && b.id === s.id));
    const overIndexInRemaining = remainingSections.findIndex((s) => s.id === over.id);
    if (overIndexInRemaining === -1) {
      return;
    }

    const overSection = remainingSections[overIndexInRemaining];
    const originalOverIndex = sections.findIndex((s) => s.id === over.id);

    const movingDown = oldIndex < originalOverIndex;
    let insertionIndex = overIndexInRemaining;

    if (movingDown && overSection) {
      const overLevel = overSection.level || 2;
      let j = overIndexInRemaining + 1;
      while (j < remainingSections.length) {
        const nextSec = remainingSections[j];
        if (nextSec && (nextSec.level || 2) > overLevel) {
          j++;
        } else {
          break;
        }
      }
      insertionIndex = j;
    } else {
      insertionIndex = overIndexInRemaining;
    }

    const newSections = [
      ...remainingSections.slice(0, insertionIndex),
      ...block,
      ...remainingSections.slice(insertionIndex),
    ];

    const reindexed: Section[] = newSections.map((s, idx) => ({ ...s, index: idx }));
    reorderSections(reindexed);

    const updates = reindexed.filter((s) => {
      const original = sections.find((orig) => orig.id === s.id);
      return original && original.index !== s.index;
    });

    await Promise.all(
      updates.map((s) => {
        if (s.id) {
          return updateTenderSectionIndex(tenderId, s.id, s.index);
        }
        return Promise.resolve();
      })
    );

    logTenderActivity.updated({
      id: tenderId,
      title: tender.title,
      updateType: 'sections_reordered',
      reason: `Reordered section "${activeSection.title}"`,
    });
  };

  return {
    sensors,
    activeId,
    visibleSections,
    expandedSectionIds,
    setExpandedSectionIds,
    handleDragStart,
    handleDragEnd,
  };
}
